import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Plus, X, Check, BarChart3, Loader2, Lock, Image as ImageIcon
} from 'lucide-react';
import { API, resolveUrl } from './shared';

const PollsTab = ({ communityId, isMember, isAdmin, polls, loading, onRefresh, currentUserId }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);
  // Sprint 1.0c V1.b — filtre actifs / clos
  const [filter, setFilter] = useState('open'); // 'open' | 'closed' | 'all'

  const filteredPolls = useMemo(() => {
    if (filter === 'all') return polls;
    if (filter === 'open') return polls.filter(p => p.is_open);
    return polls.filter(p => !p.is_open);
  }, [polls, filter]);

  const handleVote = async (pollId, optionId, currentVote, allowMultiple) => {
    // Brief B5 P0 Bug 3 — multi-choice polls : on click, ADD if not already
    // selected, REMOVE if already selected (toggle inside an array). Single-
    // choice keeps the previous toggle-or-replace behaviour.
    try {
      const current = Array.isArray(currentVote) ? currentVote : [];
      let newVote;
      if (allowMultiple) {
        newVote = current.includes(optionId)
          ? current.filter(x => x !== optionId)
          : [...current, optionId];
      } else {
        newVote = current.includes(optionId) ? [] : [optionId];
      }
      await axios.post(`${API}/communities/polls/${pollId}/vote`, { option_ids: newVote }, { withCredentials: true });
      onRefresh();
    } catch (error) { console.error(error); }
  };

  const handleClose = async (pollId) => {
    if (!window.confirm('Clôturer ce sondage ?')) return;
    try {
      await axios.post(`${API}/communities/polls/${pollId}/close`, {}, { withCredentials: true });
      onRefresh();
    } catch (error) { console.error(error); }
  };

  const counts = useMemo(() => ({
    open: polls.filter(p => p.is_open).length,
    closed: polls.filter(p => !p.is_open).length,
    all: polls.length,
  }), [polls]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* Sprint 1.0c V1.b — filter pills */}
        <div className="flex items-center gap-2" data-testid="polls-filter-pills">
          {[
            { id: 'open', label: 'Actifs', count: counts.open },
            { id: 'closed', label: 'Clôturés', count: counts.closed },
            { id: 'all', label: 'Tous', count: counts.all },
          ].map(({ id, label, count }) => (
            <button
              key={id}
              data-testid={`polls-filter-${id}`}
              onClick={() => setFilter(id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                filter === id
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-transparent shadow'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
              }`}
            >
              {label}
              <span className={`ml-1 text-xs ${filter === id ? 'text-white/80' : 'text-gray-400'}`}>{count}</span>
            </button>
          ))}
        </div>
        {isMember && (
          <Button size="sm" className="bg-gradient-to-r from-purple-500 to-pink-500 text-white" onClick={() => setShowCreate(true)} data-testid="create-poll-btn">
            <Plus className="h-4 w-4 mr-1" />Nouveau sondage
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-purple-500" /></div>
      ) : filteredPolls.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {filter === 'closed' ? 'Aucun sondage clôturé' : filter === 'open' ? 'Aucun sondage actif' : 'Aucun sondage'}
          </h3>
          <p className="text-gray-500">{isMember && filter !== 'closed' ? "Créez le premier sondage !" : "Les sondages apparaîtront ici."}</p>
        </div>
      ) : (
        filteredPolls.map(poll => {
          const closed = !poll.is_open;
          return (
            <div
              key={poll.poll_id}
              className={`bg-white rounded-2xl shadow-lg p-5 transition-opacity ${closed ? 'opacity-75' : ''}`}
              data-testid={`poll-${poll.poll_id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={poll.author_avatar} />
                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs">{poll.author_name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{poll.author_name}</p>
                  <p className="text-xs text-gray-500">{new Date(poll.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</p>
                </div>
                {closed && (
                  <span
                    className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium"
                    data-testid={`poll-closed-badge-${poll.poll_id}`}
                  >
                    <Lock className="h-3 w-3" /> Clôturé
                  </span>
                )}
                {!closed && (currentUserId === poll.user_id || isAdmin) && (
                  <>
                    <button
                      onClick={() => setEditingPoll(poll)}
                      className="text-xs text-gray-500 hover:text-purple-600 font-medium"
                      data-testid={`edit-poll-${poll.poll_id}`}
                    >
                      Modifier
                    </button>
                    <button onClick={() => handleClose(poll.poll_id)} className="text-xs text-gray-400 hover:text-red-500 font-medium" data-testid={`close-poll-${poll.poll_id}`}>
                      Clôturer
                    </button>
                  </>
                )}
              </div>

              <h4 className="font-semibold text-gray-900 text-lg">{poll.question}</h4>
              {/* Brief B5 P0 Bug 3 — hint visible quand le sondage accepte plusieurs choix */}
              {poll.allow_multiple && (
                <span
                  className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full"
                  data-testid={`poll-multi-badge-${poll.poll_id}`}
                >
                  <Check className="h-3 w-3" /> Plusieurs choix possibles
                </span>
              )}

              {/* Sprint 1.0c V1.b — description + image */}
              {poll.description && (
                <p className="text-sm text-gray-600 mt-1.5 whitespace-pre-wrap" data-testid={`poll-description-${poll.poll_id}`}>
                  {poll.description}
                </p>
              )}
              {poll.image_url && (
                <img
                  src={resolveUrl(poll.image_url)}
                  alt=""
                  className="mt-3 max-h-72 w-full object-cover rounded-xl border border-gray-100"
                  data-testid={`poll-image-${poll.poll_id}`}
                />
              )}

              {closed && (
                <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600 flex items-center gap-2" data-testid={`poll-closed-message-${poll.poll_id}`}>
                  <Lock className="h-4 w-4 text-gray-400" />
                  Ce sondage est terminé. Voici les résultats finaux.
                </div>
              )}

              <div className="space-y-2.5 mt-4">
                {poll.options.map(opt => {
                  const isVoted = poll.user_vote?.includes(opt.id);
                  const percent = poll.total_votes > 0 ? Math.round((opt.vote_count / poll.total_votes) * 100) : 0;
                  const hasVoted = poll.user_vote && poll.user_vote.length > 0;
                  const showResults = hasVoted || closed;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => !closed && isMember && handleVote(poll.poll_id, opt.id, poll.user_vote, poll.allow_multiple)}
                      disabled={closed || !isMember}
                      className={`w-full text-left relative overflow-hidden rounded-xl border-2 transition-all ${
                        isVoted ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                      } ${closed ? 'cursor-default' : ''}`}
                      data-testid={`poll-option-${opt.id}`}
                    >
                      {showResults && (
                        <div className={`absolute inset-0 transition-all duration-500 ${isVoted ? 'bg-purple-100' : 'bg-gray-100'}`} style={{ width: `${percent}%` }} />
                      )}
                      <div className="relative flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {/* Brief B5 P0 Bug 3 — checkbox shape for multi-choice, circle for single-choice */}
                          <div className={`w-4 h-4 ${poll.allow_multiple ? 'rounded' : 'rounded-full'} border-2 flex items-center justify-center shrink-0 ${isVoted ? 'border-purple-500 bg-purple-500' : 'border-gray-300'}`}>
                            {isVoted && <Check className="h-2.5 w-2.5 text-white" />}
                          </div>
                          <span className={`text-sm font-medium truncate ${isVoted ? 'text-purple-700' : 'text-gray-700'}`}>{opt.text}</span>
                        </div>
                        {showResults && (
                          <div className="flex items-center gap-2 shrink-0">
                            {/* Sprint 1.0c V1.b — Steve's spec: barres + nombre votes par option */}
                            <span className="text-xs text-gray-500" data-testid={`poll-option-votes-${opt.id}`}>
                              {opt.vote_count} vote{opt.vote_count > 1 ? 's' : ''}
                            </span>
                            <span className="text-sm font-bold text-gray-700 min-w-[2.5em] text-right">{percent}%</span>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-3 text-center">{poll.total_votes} vote{poll.total_votes > 1 ? 's' : ''} au total</p>
            </div>
          );
        })
      )}

      {showCreate && <CreatePollModal communityId={communityId} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); onRefresh(); }} />}
      {editingPoll && <EditPollModal poll={editingPoll} onClose={() => setEditingPoll(null)} onSaved={() => { setEditingPoll(null); onRefresh(); }} />}
    </div>
  );
};


const CreatePollModal = ({ communityId, onClose, onCreated }) => {
  const [question, setQuestion] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const addOption = () => { if (options.length < 6) setOptions([...options, '']); };
  const removeOption = (i) => { if (options.length > 2) setOptions(options.filter((_, idx) => idx !== i)); };
  const updateOption = (i, val) => setOptions(options.map((o, idx) => idx === i ? val : o));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/upload/image`, fd, { withCredentials: true });
      setImageUrl(r.data?.url || r.data?.image_url || '');
    } catch (err) {
      alert('Upload échoué');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    const validOpts = options.filter(o => o.trim());
    if (!question.trim() || validOpts.length < 2) return;
    setSaving(true);
    try {
      await axios.post(`${API}/communities/${communityId}/polls`, {
        question: question.trim(),
        options: validOpts,
        allow_multiple: allowMultiple,
        description: description.trim() || null,
        image_url: imageUrl || null,
      }, { withCredentials: true });
      onCreated();
    } catch (error) { console.error(error); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="create-poll-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Nouveau sondage</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Question *</label>
            <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Posez votre question..." data-testid="poll-question-input" maxLength={140} />
          </div>

          {/* Sprint 1.0c V1.b — description optionnelle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contexte / description (optionnel)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ajoutez du contexte, des liens, ou un contexte pour orienter les votes."
              rows={3}
              maxLength={500}
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-300 outline-none resize-none text-sm"
              data-testid="poll-description-input"
            />
            <p className="text-xs text-gray-400 mt-1">{description.length}/500</p>
          </div>

          {/* Sprint 1.0c V1.b — image optionnelle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image (optionnelle)</label>
            {imageUrl ? (
              <div className="relative">
                <img src={resolveUrl(imageUrl)} alt="" className="rounded-lg max-h-40 w-full object-cover" />
                <button
                  onClick={() => setImageUrl('')}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white p-1 rounded-full shadow"
                  data-testid="poll-image-remove"
                >
                  <X className="h-3.5 w-3.5 text-gray-600" />
                </button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg py-4 cursor-pointer hover:border-purple-300 text-sm text-gray-600">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                <span>{uploading ? 'Upload en cours...' : 'Ajouter une image'}</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="poll-image-input" />
              </label>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Options ({options.length}/6)</label>
            <div className="space-y-2">
              {options.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={opt} onChange={(e) => updateOption(i, e.target.value)} placeholder={`Option ${i + 1}`} className="flex-1" data-testid={`poll-option-input-${i}`} maxLength={80} />
                  {options.length > 2 && <button onClick={() => removeOption(i)} className="text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button>}
                </div>
              ))}
              {options.length < 6 && (
                <button onClick={addOption} className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1">
                  <Plus className="h-3.5 w-3.5" />Ajouter une option
                </button>
              )}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)} className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
            <span className="text-sm text-gray-700">Autoriser les choix multiples</span>
          </label>
        </div>
        <div className="p-6 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !question.trim() || options.filter(o => o.trim()).length < 2}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white"
            data-testid="submit-poll-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}Créer
          </Button>
        </div>
      </div>
    </div>
  );
};

// Sprint 1.0c V1.a-bis hotfix #4 (Bug 4) — Edit an OPEN poll
const EditPollModal = ({ poll, onClose, onSaved }) => {
  const [description, setDescription] = useState(poll.description || '');
  const [imageUrl, setImageUrl] = useState(poll.image_url || '');
  const [question, setQuestion] = useState(poll.question || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const questionLocked = (poll.total_votes || 0) > 0;

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/upload/image`, fd, { withCredentials: true });
      setImageUrl(r.data?.url || r.data?.image_url || '');
    } catch (err) {
      alert('Upload échoué');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        description: description.trim() || null,
        image_url: imageUrl || null,
      };
      if (!questionLocked && question.trim() !== poll.question) {
        payload.question = question.trim();
      }
      await axios.patch(`${API}/communities/polls/${poll.poll_id}`, payload, { withCredentials: true });
      onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Modification impossible');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-poll-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Modifier le sondage</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              disabled={questionLocked}
              maxLength={140}
              data-testid="edit-poll-question-input"
            />
            {questionLocked && (
              <p className="text-xs text-amber-700 mt-1">La question est verrouillée car des votes ont déjà été enregistrés.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contexte / description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-300 outline-none resize-none text-sm"
              data-testid="edit-poll-description-input"
            />
            <p className="text-xs text-gray-400 mt-1">{description.length}/500</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
            {imageUrl ? (
              <div className="relative">
                <img src={resolveUrl(imageUrl)} alt="" className="rounded-lg max-h-40 w-full object-cover" />
                <button
                  onClick={() => setImageUrl('')}
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white p-1 rounded-full shadow"
                  data-testid="edit-poll-image-remove"
                >
                  <X className="h-3.5 w-3.5 text-gray-600" />
                </button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg py-4 cursor-pointer hover:border-purple-300 text-sm text-gray-600">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                <span>{uploading ? 'Upload en cours...' : 'Ajouter une image'}</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </label>
            )}
          </div>

          <p className="text-xs text-gray-500 italic">
            Les options de vote ne sont jamais modifiables, par souci d'intégrité des votes.
          </p>
        </div>
        <div className="p-6 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white"
            data-testid="submit-edit-poll-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PollsTab;